import { Box, Grid, GridItem, VStack, usePrevious } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { LDFlags } from '../../../../../../constants/flags';
import { useFlag, useOnce, useOnceWhen } from '../../../../../../hooks';
import {
  trackChecklistCompleted,
  trackConversationOpened,
  trackPostAcceptanceHubViewed,
  trackStageAdvanced,
} from '../../../../../../services/analytics/marketplaceEvents';
import { GetLeadByExternalId200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { MarketplaceVehicle } from '../../../../../../utils/cars';
import ConversationThreadDrawer from '../conversation/ConversationThreadDrawer';
import { getUnreadCount } from '../conversation/utils';
import CarPhotosPanel from './CarPhotosPanel';
import PostAcceptanceConversationCard from './PostAcceptanceConversationCard';
import PostAcceptanceDocumentsPanel from './PostAcceptanceDocumentsPanel';
import PostAcceptanceHeader from './PostAcceptanceHeader';
import PostAcceptanceInactive from './PostAcceptanceInactive';
import SaleProgressTimeline from './SaleProgressTimeline';
import { isOffRampStatus } from './postAcceptanceStatus';
import TodoPanel from './todos/TodoPanel';
import { useTodoList } from './todos/useTodoList';
import { AcceptedOffer, STAGE_INDEX, buildWonTarget, currentStageKey, getWonThread } from './utils';

type FinishYourSaleHubProps = {
  dealId: string;
  lead: GetLeadByExternalId200;
  acceptedOffer?: AcceptedOffer;
  externalId: string;
  vehicle: MarketplaceVehicle;
};

const ActiveSaleHub = ({
  dealId,
  lead,
  acceptedOffer,
  externalId,
  vehicle,
}: FinishYourSaleHubProps) => {
  const messagingEnabled = useFlag(LDFlags.MARKETPLACE_MESSAGING);
  const [isConversationOpen, setIsConversationOpen] = useState(false);
  const todoList = useTodoList(dealId, lead);

  // Null until todo statuses load, so a revisit's initial remaining drop never counts.
  const previousRemaining = usePrevious(todoList.statusReady ? todoList.remaining : null);
  useOnceWhen(todoList.isFinished && previousRemaining != null && previousRemaining > 0, () =>
    trackChecklistCompleted({ dealId, leadId: lead.id }),
  );

  const wonTarget = messagingEnabled ? buildWonTarget(acceptedOffer, lead) : null;
  const wonThread = getWonThread(wonTarget, lead.messages);
  const unreadCount = getUnreadCount(wonThread, wonTarget?.history);

  const handleOpenConversation =
    wonTarget != null
      ? () => {
          trackConversationOpened(lead.id, wonTarget);
          setIsConversationOpen(true);
        }
      : undefined;
  const handleCloseConversation = () => setIsConversationOpen(false);

  const conversationCard = (
    <PostAcceptanceConversationCard
      dealershipName={acceptedOffer?.orgName}
      repName={acceptedOffer?.repName}
      thread={wonThread}
      unreadCount={unreadCount}
      onOpen={handleOpenConversation}
    />
  );

  return (
    <VStack align="stretch" spacing={{ base: '14px', md: 5 }}>
      <PostAcceptanceHeader vehicle={vehicle} offer={acceptedOffer} />
      <SaleProgressTimeline
        lead={lead}
        requiredRemaining={todoList.remaining}
        acceptedOffer={acceptedOffer}
      />
      <Grid
        templateColumns={{ base: 'minmax(0, 1fr)', md: 'minmax(0, 1.55fr) minmax(0, 1fr)' }}
        gap={{ base: '14px', md: 5 }}
        alignItems="start"
      >
        <GridItem minW={0}>
          <VStack align="stretch" spacing={{ base: '14px', md: 5 }}>
            <Box display={{ base: 'block', md: 'none' }}>{conversationCard}</Box>
            <TodoPanel
              todoList={todoList}
              dealershipName={acceptedOffer?.orgName || 'the dealership'}
            />
          </VStack>
        </GridItem>

        <GridItem minW={0}>
          <VStack align="stretch" spacing={{ base: '14px', md: 4 }}>
            <Box display={{ base: 'none', md: 'block' }}>{conversationCard}</Box>
            <PostAcceptanceDocumentsPanel dealId={dealId} defaultCollapsed={todoList.isFinished} />
            {todoList.showCarPhotosPanel && <CarPhotosPanel dealId={dealId} />}
          </VStack>
        </GridItem>

        <ConversationThreadDrawer
          target={isConversationOpen ? wonTarget : null}
          thread={wonThread}
          externalId={externalId}
          leadId={lead.id}
          onClose={handleCloseConversation}
        />
      </Grid>
    </VStack>
  );
};

const FinishYourSaleHub = (props: FinishYourSaleHubProps) => {
  const { dealId, lead, vehicle } = props;

  useOnce(() => trackPostAcceptanceHubViewed({ dealId, leadId: lead.id, leadStatus: lead.status }));

  // Fire once per forward stage move as the poll advances the status. Same-stage
  // moves (active -> waiting) and off-ramp transitions (which map back to Finish)
  // are not advances and stay silent.
  const previousStatus = usePrevious(lead.status);
  useEffect(() => {
    if (!previousStatus || previousStatus === lead.status) return;
    const fromStage = currentStageKey(previousStatus);
    const toStage = currentStageKey(lead.status);
    const advanced = STAGE_INDEX[toStage] > STAGE_INDEX[fromStage];
    if (advanced) {
      trackStageAdvanced({ dealId, leadId: lead.id, fromStage, toStage });
    }
  }, [previousStatus, lead.status, lead.id, dealId]);

  // Branch before ActiveSaleHub mounts so its document/status queries never
  // fire for off-ramp (canceled/returned) sales.
  if (isOffRampStatus(lead.status)) {
    return <PostAcceptanceInactive vehicle={vehicle} />;
  }

  return <ActiveSaleHub {...props} />;
};

export default FinishYourSaleHub;
